import {
	clearFetchError,
	createMobileFiltersController,
	getPageFromLink,
	isMobileViewport,
	readUrlListingState,
	showFetchError,
	syncUrlParams,
} from '../utils/filterable-archive.js';
import { initImageLightbox } from '../utils/image-lightbox.js';

export class PhotoGallery {
	constructor() {
		document.querySelectorAll('.photo-gallery[data-ajax="1"]').forEach((root) => {
			this.bind(root);
		});
	}

	bind(root) {
		const config = window.icarPhotoGallery;

		if (!config?.ajaxUrl) {
			return;
		}

		const results = root.querySelector('.photo-gallery__results');

		if (!results) {
			return;
		}

		const instanceId = root.dataset.instance || '1';
		const defaultPerPage = root.dataset.defaultPerPage || '9';
		const activeFilters = root.querySelector('[data-photo-gallery-active-filters]');
		let pendingFilterSnapshot = null;
		let skipRevertOnClose = false;

		const bindLightbox = () => {
			initImageLightbox(root.querySelector('.photo-gallery__main'));
		};

		const getSelectedTags = () =>
			[...root.querySelectorAll('.photo-gallery__tag-input:checked')].map((input) => input.value);

		const snapshotFilters = () => ({
			s: root.querySelector('.photo-gallery__search-input')?.value ?? '',
			tags: getSelectedTags(),
		});

		const restoreFilterSnapshot = (snapshot) => {
			const searchInput = root.querySelector('.photo-gallery__search-input');

			if (searchInput) {
				searchInput.value = snapshot.s;
			}

			root.querySelectorAll('.photo-gallery__tag-input').forEach((input) => {
				input.checked = snapshot.tags.includes(input.value);
			});
		};

		const { filtersBackdrop, setFiltersOpen } = createMobileFiltersController(root, {
			blockName: 'photo-gallery',
			backdropSelector: '[data-photo-gallery-filters-backdrop]',
			toggleSelector: '[data-photo-gallery-open-filters]',
			panelSelector: '.photo-gallery__filters-panel',
			closeSelector: '[data-photo-gallery-close-filters]',
			onWillClose: () => {
				if (!skipRevertOnClose && pendingFilterSnapshot) {
					restoreFilterSnapshot(pendingFilterSnapshot);
				}

				pendingFilterSnapshot = null;
				skipRevertOnClose = false;
			},
		});

		const setFiltersOpenWithRevert = (open, { keepChanges = false } = {}) => {
			if (open && isMobileViewport()) {
				pendingFilterSnapshot = snapshotFilters();
			}

			if (!open) {
				skipRevertOnClose = keepChanges;
			}

			setFiltersOpen(open);
		};

		const updateActiveFilters = (html) => {
			if (!activeFilters || html === undefined) {
				return;
			}

			activeFilters.innerHTML = html;
		};

		const appendTagsToBody = (body, tags) => {
			tags.forEach((slug) => {
				body.append('photo_gallery_tags[]', slug);
			});
		};

		const applyStateToForm = (state) => {
			const searchInput = root.querySelector('.photo-gallery__search-input');
			const perPageSelect = root.querySelector('.photo-gallery__per-page-select');

			if (searchInput) {
				searchInput.value = state.s || '';
			}

			if (perPageSelect) {
				perPageSelect.value = state.posts_per_page || defaultPerPage;
			}

			const tags = state.photo_gallery_tags || [];

			root.querySelectorAll('.photo-gallery__tag-input').forEach((input) => {
				input.checked = tags.includes(input.value);
			});
		};

		const fetchResults = async (params, { updateUrl = true, scroll = false, closeFilters = false } = {}) => {
			root.classList.add('is-loading');
			clearFetchError(results, 'photo-gallery');

			const body = new FormData();
			body.append('action', config.action);
			body.append('nonce', config.nonce);
			body.append('default_per_page', defaultPerPage);
			body.append('instance_id', instanceId);

			const tags = params.photo_gallery_tags || [];

			Object.entries(params).forEach(([key, value]) => {
				if (key === 'photo_gallery_tags' || value === undefined || value === null || value === '') {
					return;
				}

				body.append(key, String(value));
			});

			appendTagsToBody(body, tags);

			try {
				const response = await fetch(config.ajaxUrl, {
					method: 'POST',
					body,
					credentials: 'same-origin',
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}

				const payload = await response.json();

				if (!payload?.success) {
					throw new Error('Request failed');
				}

				const main = results.querySelector('.photo-gallery__main');

				if (main && payload.data.html) {
					main.outerHTML = payload.data.html;
				}

				if (payload.data.footerHtml) {
					const footer = root.querySelector('[data-photo-gallery-footer]');

					if (footer) {
						footer.innerHTML = payload.data.footerHtml;
					}
				}

				if (payload.data.countHtml) {
					const count = root.querySelector('.photo-gallery__sidebar .photo-gallery__count');

					if (count) {
						count.outerHTML = payload.data.countHtml;
					}
				}

				updateActiveFilters(payload.data.activeFiltersHtml);
				bindLightbox();

				if (updateUrl) {
					this.updateUrl(params, defaultPerPage);
				}

				if (scroll) {
					root.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}

				if (closeFilters) {
					setFiltersOpenWithRevert(false, { keepChanges: true });
				}
			} catch {
				showFetchError(
					results,
					'photo-gallery',
					config.fetchError || 'Unable to load photos. Please try again.'
				);
			} finally {
				root.classList.remove('is-loading');
			}
		};

		const getCurrentState = (paged = '1') => {
			const searchInput = root.querySelector('.photo-gallery__search-input');
			const perPageSelect = root.querySelector('.photo-gallery__per-page-select');

			return {
				s: searchInput?.value?.trim() || '',
				photo_gallery_tags: getSelectedTags(),
				posts_per_page: perPageSelect?.value || defaultPerPage,
				paged: String(paged),
			};
		};

		const clearTagInputs = () => {
			root.querySelectorAll('.photo-gallery__tag-input').forEach((input) => {
				input.checked = false;
			});
		};

		const uncheckTag = (slug) => {
			root.querySelectorAll(`.photo-gallery__tag-input[value="${slug}"]`).forEach((input) => {
				input.checked = false;
			});
		};

		const onPopState = () => {
			if (!root.isConnected) {
				return;
			}

			const state = readUrlListingState(defaultPerPage, {
				topicParam: 'photo_gallery_tags',
				topicStateKey: 'photo_gallery_tags',
			});
			applyStateToForm(state);
			fetchResults(state, { updateUrl: false });
		};

		window.addEventListener('popstate', onPopState);

		root.addEventListener('click', (event) => {
			const openButton = event.target.closest('[data-photo-gallery-open-filters]');

			if (openButton && root.contains(openButton)) {
				event.preventDefault();
				setFiltersOpenWithRevert(true);
				return;
			}

			const closeButton = event.target.closest('[data-photo-gallery-close-filters]');

			if (closeButton && root.contains(closeButton)) {
				event.preventDefault();
				setFiltersOpenWithRevert(false);
				return;
			}

			if (event.target === filtersBackdrop) {
				event.preventDefault();
				setFiltersOpenWithRevert(false);
				return;
			}

			const removeSearch = event.target.closest('[data-photo-gallery-remove-search]');

			if (removeSearch && root.contains(removeSearch)) {
				event.preventDefault();

				const searchInput = root.querySelector('.photo-gallery__search-input');

				if (searchInput) {
					searchInput.value = '';
				}

				const state = getCurrentState();
				state.s = '';

				fetchResults(state, { scroll: true });
				return;
			}

			const removeTag = event.target.closest('[data-photo-gallery-remove-tag]');

			if (removeTag && root.contains(removeTag)) {
				event.preventDefault();

				const slug = removeTag.getAttribute('data-photo-gallery-remove-tag');

				if (slug) {
					uncheckTag(slug);
				}

				fetchResults(getCurrentState(), { scroll: true });
				return;
			}

			const clearButton = event.target.closest('[data-photo-gallery-clear]');

			if (clearButton && root.contains(clearButton)) {
				event.preventDefault();

				const searchInput = root.querySelector('.photo-gallery__search-input');
				const perPageSelect = root.querySelector('.photo-gallery__per-page-select');

				if (searchInput) {
					searchInput.value = '';
				}

				if (perPageSelect) {
					perPageSelect.value = defaultPerPage;
				}

				clearTagInputs();

				fetchResults(
					{
						s: '',
						photo_gallery_tags: [],
						posts_per_page: defaultPerPage,
						paged: '1',
					},
					{ scroll: true, closeFilters: true }
				);
				return;
			}

			const applyButton = event.target.closest('[data-photo-gallery-apply-filters]');

			if (applyButton && root.contains(applyButton)) {
				event.preventDefault();
				fetchResults(getCurrentState(), { scroll: true, closeFilters: true });
				return;
			}

			const copyButton = event.target.closest('.share-social__copy');

			if (copyButton && root.contains(copyButton)) {
				event.preventDefault();
				const link = copyButton.dataset.link;
				if (!link) {
					return;
				}
				navigator.clipboard.writeText(link);
				return;
			}

			const pageLink = event.target.closest('.photo-gallery__pagination a');

			if (pageLink && root.contains(pageLink)) {
				event.preventDefault();

				const state = getCurrentState(getPageFromLink(pageLink));
				fetchResults(state, { scroll: true });
			}
		});

		const searchForm = root.querySelector('.photo-gallery__search');
		const searchInput = root.querySelector('.photo-gallery__search-input');

		if (searchForm) {
			searchForm.addEventListener('submit', (event) => {
				event.preventDefault();
				fetchResults(getCurrentState(), { scroll: true, closeFilters: true });
			});
		}

		if (searchInput) {
			let lastSearch = searchInput.value.trim();

			searchInput.addEventListener('input', () => {
				const value = searchInput.value.trim();

				if (value === '' && lastSearch !== '') {
					const state = getCurrentState();
					state.s = '';
					fetchResults(state);
				}

				lastSearch = value;
			});
		}

		root.addEventListener('change', (event) => {
			const tagInput = event.target.closest('.photo-gallery__tag-input');

			if (tagInput && root.contains(tagInput) && !isMobileViewport()) {
				fetchResults(getCurrentState(), { scroll: true });
				return;
			}

			const select = event.target.closest('.photo-gallery__per-page-select');

			if (!select || !root.contains(select)) {
				return;
			}

			fetchResults(getCurrentState(), { scroll: true });
		});

		bindLightbox();
	}

	updateUrl(params, defaultPerPage) {
		const url = new URL(window.location.href);

		syncUrlParams(url, params, defaultPerPage, ['photo_gallery_tags'], (targetUrl, state) => {
			if (state.photo_gallery_tags?.length) {
				targetUrl.searchParams.set('photo_gallery_tags', state.photo_gallery_tags.join(','));
			}
		});

		window.history.pushState({}, '', url);
	}
}
